import React, { useState, useEffect } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Button,
	Chip,
	LinearProgress,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	Snackbar,
	Tabs,
	Tab,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Switch,
	FormControlLabel,
	Slider,
} from "@mui/material";
import {
	PlayArrow,
	Pause,
	CheckCircle,
	Schedule,
	Search,
	Add,
	Download,
	Visibility,
	Error,
	Refresh,
} from "@mui/icons-material";
import { format } from "date-fns";
import {
	complianceApi,
	ComplianceWorkflow,
	ComplianceStep,
	ComplianceForm,
	ServiceProvider,
	StandardsCatalogEntry,
} from "../services/complianceApi";

interface ComplianceDashboardProps {
  initialWorkflowId?: string;
  initialTab?: number;
}

const ComplianceDashboard: React.FC<ComplianceDashboardProps> = ({ initialWorkflowId, initialTab }) => {
  // State
  const [workflows, setWorkflows] = useState<ComplianceWorkflow[]>([]);
	const [selectedWorkflow, setSelectedWorkflow] =
		useState<ComplianceWorkflow | null>(null);
	const [workflowSteps, setWorkflowSteps] = useState<ComplianceStep[]>([]);
	const [forms, setForms] = useState<ComplianceForm[]>([]);
	const [providers, setProviders] = useState<ServiceProvider[]>([]);
	const [loading, setLoading] = useState(false);
	const [stepsLoading, setStepsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState(initialTab ?? 0);
	const [createWorkflowOpen, setCreateWorkflowOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");

	// Standards catalog (admin)
	const [standards, setStandards] = useState<StandardsCatalogEntry[]>([]);
	const [standardsLoading, setStandardsLoading] = useState(false);
	const [standardsActionLoading, setStandardsActionLoading] = useState(false);
	const [maxPages, setMaxPages] = useState<number>(35);

	// Chat workflow automation (admin setting; stored locally in browser)
	const [autoCreateWorkflowEnabled, setAutoCreateWorkflowEnabled] = useState<boolean>(() => {
		return localStorage.getItem("gamora_workflow_auto_create_enabled") === "true";
	});
	const [autoCreateWorkflowThreshold, setAutoCreateWorkflowThreshold] = useState<number>(() => {
		const raw = localStorage.getItem("gamora_workflow_auto_create_threshold");
		const n = raw ? parseInt(raw, 10) : 5;
		return Number.isFinite(n) ? n : 5;
	});

  // Form state for creating workflow
	const [newWorkflow, setNewWorkflow] = useState({
		name: "",
		jurisdiction: "",
		device_class: "",
		device_type: "",
		device_category: "",
	});

  // Load data on component mount
	useEffect(() => {
		loadWorkflows();
		loadForms();
		loadProviders();
		loadStandardsCatalog();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handle initial workflow selection
	useEffect(() => {
		if (initialWorkflowId && workflows.length > 0) {
			const workflow = workflows.find(w => w.id === initialWorkflowId);
			if (workflow) {
				setSelectedWorkflow(workflow);
				loadWorkflowSteps(workflow.id);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialWorkflowId, workflows]);

	// Handle initial tab
	useEffect(() => {
		if (initialTab !== undefined) {
			setActiveTab(initialTab);
		}
	}, [initialTab]);

  // API calls
	const loadWorkflows = async () => {
		try {
			setLoading(true);
			const data = await complianceApi.getWorkflows();
			setWorkflows(data.workflows);
			if (data.workflows.length === 0) {
				setSelectedWorkflow(null);
				setWorkflowSteps([]);
				return;
			}

			// If initialWorkflowId is provided, try to select it
			if (initialWorkflowId) {
				const initialWorkflow = data.workflows.find(
					(workflow) => workflow.id === initialWorkflowId
				);
				if (initialWorkflow) {
					setSelectedWorkflow(initialWorkflow);
					await loadWorkflowSteps(initialWorkflow.id);
					return;
				}
			}

			const existingSelection = data.workflows.find(
				(workflow) => workflow.id === selectedWorkflow?.id
			);

			const workflowToSelect = existingSelection || data.workflows[0];
			setSelectedWorkflow(workflowToSelect);
			await loadWorkflowSteps(workflowToSelect.id);
		} catch (err) {
			setError("Failed to load workflows");
		} finally {
			setLoading(false);
		}
	};

	const loadWorkflowSteps = async (workflowId: string) => {
		try {
			setStepsLoading(true);
			const response = await complianceApi.getWorkflowStatus(workflowId);
			setWorkflowSteps(response.steps);
		} catch (err) {
			setError("Failed to load workflow steps");
			setWorkflowSteps([]);
		} finally {
			setStepsLoading(false);
		}
	};

	const loadForms = async () => {
		try {
			const response = await complianceApi.getFormTemplates();
			setForms(response.forms);
		} catch (err) {
			setError("Failed to load forms");
		}
	};

	const loadProviders = async () => {
		try {
			const providerList = await complianceApi.getServiceProviders();
			setProviders(providerList);
		} catch (err) {
			setError("Failed to load service providers");
		}
	};

	const loadStandardsCatalog = async () => {
		try {
			setStandardsLoading(true);
			const response = await complianceApi.listStandardsCatalog();
			setStandards(Array.isArray(response.standards) ? response.standards : []);
		} catch (err) {
			setError("Failed to load standards catalog");
			setStandards([]);
		} finally {
			setStandardsLoading(false);
		}
	};

	const handleFetchStandardsCatalog = async () => {
		try {
			setStandardsActionLoading(true);
			const res = await complianceApi.fetchStandardsCatalog();
			setSuccess(`Standards catalog updated (created ${res.created}, updated ${res.updated}).`);
			await loadStandardsCatalog();
		} catch (err) {
			setError("Failed to update standards catalog");
		} finally {
			setStandardsActionLoading(false);
		}
	};

	const handleExtractClauses = async (standardCode: string) => {
		try {
			setStandardsActionLoading(true);
			await complianceApi.extractStandardClausesFromPdf(standardCode, { replace: true, max_pages: maxPages });
			setSuccess(`Clauses extracted for ${standardCode}.`);
			await loadStandardsCatalog();
		} catch (err) {
			setError(`Failed to extract clauses for ${standardCode}`);
		} finally {
			setStandardsActionLoading(false);
		}
	};

	const handleBulkExtractClauses = async () => {
		try {
			setStandardsActionLoading(true);
			// Process in sequence to avoid overloading the server and to keep progress deterministic.
			for (const s of standards) {
				await complianceApi.extractStandardClausesFromPdf(s.code, { replace: true, max_pages: maxPages });
			}
			setSuccess("Clause extraction complete for all standards.");
			await loadStandardsCatalog();
		} catch (err) {
			setError("Bulk clause extraction failed");
		} finally {
			setStandardsActionLoading(false);
		}
	};

  // Event handlers
	const handleWorkflowSelect = (workflow: ComplianceWorkflow) => {
		setSelectedWorkflow(workflow);
		loadWorkflowSteps(workflow.id);
	};

	const handleStartWorkflow = async (workflowId: string) => {
		try {
			setLoading(true);
			await complianceApi.startWorkflow(workflowId);
			setSuccess("Workflow started successfully");
			await loadWorkflows();
		} catch (err) {
			setError("Failed to start workflow");
		} finally {
			setLoading(false);
		}
	};

	const handlePauseWorkflow = async (workflowId: string) => {
		try {
			setLoading(true);
			await complianceApi.pauseWorkflow(workflowId);
			setSuccess("Workflow paused successfully");
			await loadWorkflows();
		} catch (err) {
			setError("Failed to pause workflow");
		} finally {
			setLoading(false);
		}
	};

	const handleCreateWorkflow = async () => {
		if (!newWorkflow.name || !newWorkflow.jurisdiction || !newWorkflow.device_class) {
			setError("Please fill in the required fields");
			return;
		}

		try {
			setLoading(true);
			await complianceApi.createWorkflow(newWorkflow);
			setSuccess("Workflow created successfully");
			setCreateWorkflowOpen(false);
			setNewWorkflow({
				name: "",
				jurisdiction: "",
				device_class: "",
				device_type: "",
				device_category: "",
			});
			await loadWorkflows();
		} catch (err) {
			setError("Failed to create workflow");
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadForm = async (formId: string) => {
		try {
			const file = await complianceApi.downloadForm(formId);
			const byteCharacters = atob(file.content);
			const byteNumbers = new Array(byteCharacters.length);
			for (let i = 0; i < byteCharacters.length; i += 1) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			const blob = new Blob([byteArray], { type: file.content_type });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = file.filename;
			link.click();
			window.URL.revokeObjectURL(url);
			setSuccess("Form downloaded successfully");
		} catch (err) {
			setError("Failed to download form");
		}
	};

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'active':
        return '#dcfce7';
      case 'paused':
      case 'draft':
      case 'pending':
        return '#fef9c3';
      case 'cancelled':
      case 'error':
        return '#fef2f2';
      default:
        return '#f3f4f6';
    }
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <Schedule color="primary" />;
      case 'blocked':
        return <Error color="error" />;
      case 'pending':
        return <Schedule color="disabled" />;
      default:
        return <Schedule color="disabled" />;
    }
  };

  // Render methods
  const renderWorkflowCard = (workflow: ComplianceWorkflow) => (
    <Card
      key={workflow.id}
      sx={{
        mb: 2,
        cursor: 'pointer',
        bgcolor: '#fff',
        border: '1px solid #eceef1',
        borderRadius: '12px',
        boxShadow: 'none',
      }}
      onClick={() => handleWorkflowSelect(workflow)}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }} component="div">
            {workflow.name}
          </Typography>
          <Chip
            label={workflow.status.toUpperCase()}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              borderRadius: '6px',
              bgcolor: getStatusBgColor(workflow.status),
              color: '#111827',
              fontWeight: 500,
            }}
          />
        </Box>

        <Box mb={2}>
          <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }} gutterBottom>
            Progress: {workflow.completion_percentage}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={workflow.completion_percentage}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: '#f3f4f6',
              '& .MuiLinearProgress-bar': { bgcolor: '#111827' },
            }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
            {workflow.estimated_duration_days} days estimated
          </Typography>
          {workflow.target_completion_date && (
            <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
              Due: {format(new Date(workflow.target_completion_date), 'MMM dd, yyyy')}
            </Typography>
          )}
        </Box>

        <Box display="flex" gap={1} mt={2}>
          {workflow.status === 'draft' && (
            <Button
              size="small"
              startIcon={<PlayArrow />}
              onClick={(e) => {
                e.stopPropagation();
                handleStartWorkflow(workflow.id);
              }}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                bgcolor: '#111827',
                color: '#fff',
                '&:hover': { bgcolor: '#1f2937' },
              }}
            >
              Start
            </Button>
          )}
          {workflow.status === 'active' && (
            <Button
              size="small"
              startIcon={<Pause />}
              onClick={(e) => {
                e.stopPropagation();
                handlePauseWorkflow(workflow.id);
              }}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                borderColor: '#e5e7eb',
                color: '#6b7280',
                '&:hover': { borderColor: '#e5e7eb', bgcolor: '#f8f9fb' },
              }}
              variant="outlined"
            >
              Pause
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

	const renderWorkflowSteps = () => (
		<Box
			sx={{
				bgcolor: '#fff',
				border: '1px solid #eceef1',
				borderRadius: '12px',
				boxShadow: 'none',
				p: 3,
			}}
		>
			<Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 2 }} component="div">
				Workflow Steps
			</Typography>
			{stepsLoading && (
				<Box display="flex" justifyContent="center" my={2}>
					<CircularProgress size={24} />
				</Box>
			)}
			{!stepsLoading && (
				<List disablePadding>
					{workflowSteps.map((step, index) => (
						<React.Fragment key={step.id}>
							<ListItem sx={{ py: 1, px: 0, '&:hover': { bgcolor: '#f8f9fb' } }}>
								<ListItemIcon sx={{ minWidth: 36 }}>{getStepStatusIcon(step.status)}</ListItemIcon>
								<ListItemText
									primary={step.title}
									primaryTypographyProps={{ sx: { fontSize: '0.82rem', color: '#374151', fontWeight: 500 } }}
									secondary={
										<Box>
											<Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
												Step {step.step_number} • {step.estimated_days} days
												estimated
												{step.actual_days && ` • ${step.actual_days} days actual`}
											</Typography>
											{step.due_date && (
												<Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
													Due: {format(new Date(step.due_date), "MMM dd, yyyy")}
												</Typography>
											)}
											{step.required_forms.length > 0 && (
												<Box mt={1}>
													<Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
														Required Forms: {step.required_forms.join(", ")}
													</Typography>
												</Box>
											)}
										</Box>
									}
									secondaryTypographyProps={{ component: 'div' }}
								/>
							</ListItem>
							{index < workflowSteps.length - 1 && <Divider sx={{ borderColor: '#f3f4f6' }} />}
						</React.Fragment>
					))}
					{workflowSteps.length === 0 && (
						<ListItem sx={{ py: 3 }}>
							<ListItemText
								primary="No steps available"
								secondary="Select a workflow to see detailed steps."
								primaryTypographyProps={{ sx: { fontSize: '0.82rem', color: '#374151' } }}
								secondaryTypographyProps={{ sx: { fontSize: '0.8rem', color: '#9ca3af' } }}
							/>
						</ListItem>
					)}
				</List>
			)}
		</Box>
	);

  const renderForms = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
          Compliance Forms
        </Typography>
        <TextField
          size="small"
          placeholder="Search forms..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: '#f8f9fb',
            },
          }}
          InputProps={{
            startAdornment: <Search sx={{ color: '#9ca3af', mr: 1, fontSize: 20 }} />,
          }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
        {forms
          .filter(form =>
            form.form_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            form.form_code.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((form) => (
            <Box key={form.id}>
              <Card
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #eceef1',
                  borderRadius: '12px',
                  boxShadow: 'none',
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }} component="div">
                      {form.form_code}
                    </Typography>
                    <Chip
                      label={form.jurisdiction.toUpperCase()}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        borderRadius: '6px',
                        bgcolor: '#f3f4f6',
                        color: '#6b7280',
                        fontWeight: 500,
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontSize: '0.82rem', color: '#374151', mb: 1 }} component="div">
                    {form.form_name}
                  </Typography>

                  <Typography paragraph sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {form.form_description}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                      {form.form_type} • {form.form_category}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Download />}
                      onClick={() => handleDownloadForm(form.id)}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        bgcolor: '#111827',
                        color: '#fff',
                        '&:hover': { bgcolor: '#1f2937' },
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
      </Box>
    </Box>
  );

  const renderProviders = () => (
    <Box>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 2 }}>
        Service Providers
      </Typography>

      {loading && providers.length === 0 && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!loading && providers.length === 0 && (
        <Box
          sx={{
            border: '1px solid #eceef1',
            borderRadius: '12px',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
            No service providers found.
          </Typography>
        </Box>
      )}

      {providers.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
          {providers.map((provider) => (
            <Box key={provider.id}>
              <Card
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #eceef1',
                  borderRadius: '12px',
                  boxShadow: 'none',
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }} component="div">
                      {provider.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                        {provider.rating}/5
                      </Typography>
                      <Chip
                        label={provider.availability}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          borderRadius: '6px',
                          bgcolor: provider.availability === 'available' ? '#dcfce7' : '#fef9c3',
                          color: '#111827',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: '0.8rem', color: '#111827', fontWeight: 600, mb: 1 }}>
                    {provider.provider_type.replace('_', ' ').toUpperCase()}
                  </Typography>

                  <Typography paragraph sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                    {provider.description}
                  </Typography>

                  <Box mb={2}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                      Specializations:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {provider.specializations.slice(0, 3).map((spec, index) => (
                        <Chip
                          key={index}
                          label={spec}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            borderRadius: '6px',
                            borderColor: '#e5e7eb',
                            color: '#6b7280',
                          }}
                        />
                      ))}
                      {provider.specializations.length > 3 && (
                        <Chip
                          label={`+${provider.specializations.length - 3} more`}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            borderRadius: '6px',
                            borderColor: '#e5e7eb',
                            color: '#6b7280',
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                      {provider.location?.city || '—'}, {provider.location?.country || '—'}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      href={provider.website}
                      target="_blank"
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        borderColor: '#e5e7eb',
                        color: '#6b7280',
                        '&:hover': { borderColor: '#e5e7eb', bgcolor: '#f8f9fb' },
                      }}
                      variant="outlined"
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderCreateWorkflowDialog = () => (
    <Dialog
      open={createWorkflowOpen}
      onClose={() => setCreateWorkflowOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 650, color: '#111827' }}>
        Create New Compliance Workflow
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Workflow Name"
            value={newWorkflow.name}
            onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#f8f9fb',
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Regulation</InputLabel>
              <Select
                value={newWorkflow.jurisdiction}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, jurisdiction: e.target.value })}
                label="Regulation"
                sx={{
                  borderRadius: '10px',
                  bgcolor: '#f8f9fb',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                }}
              >
                <MenuItem value="eu_mdr">EU MDR</MenuItem>
                <MenuItem value="us_fda">US FDA</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Device Class</InputLabel>
              <Select
                value={newWorkflow.device_class}
                onChange={(e) => setNewWorkflow({ ...newWorkflow, device_class: e.target.value })}
                label="Device Class"
                sx={{
                  borderRadius: '10px',
                  bgcolor: '#f8f9fb',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                }}
              >
                <MenuItem value="class_i">Class I</MenuItem>
                <MenuItem value="class_iia">Class IIa</MenuItem>
                <MenuItem value="class_iib">Class IIb</MenuItem>
                <MenuItem value="class_iii">Class III</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              label="Device Type"
              value={newWorkflow.device_type}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, device_type: e.target.value })}
              sx={{
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#f8f9fb',
                },
              }}
            />

            <TextField
              fullWidth
              label="Device Category"
              value={newWorkflow.device_category}
              onChange={(e) => setNewWorkflow({ ...newWorkflow, device_category: e.target.value })}
              sx={{
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#f8f9fb',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setCreateWorkflowOpen(false)}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            borderColor: '#e5e7eb',
            color: '#6b7280',
            '&:hover': { borderColor: '#e5e7eb', bgcolor: '#f8f9fb' },
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateWorkflow}
          disabled={loading}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            bgcolor: '#111827',
            color: '#fff',
            '&:hover': { bgcolor: '#1f2937' },
          }}
        >
          Create Workflow
        </Button>
      </DialogActions>
    </Dialog>
  );

	const renderStandardsCatalog = () => {
		return (
			<Box>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
					<Box>
						<Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
							Standards Catalog
						</Typography>
						<Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', mt: 0.5 }}>
							Local-first: extract clauses from ingested PDFs. Optional: refresh harmonised standards list from OJEU.
						</Typography>
					</Box>
					<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
						<TextField
							label="Max TOC pages"
							type="number"
							size="small"
							value={maxPages}
							onChange={(e) => setMaxPages(Math.max(1, Math.min(200, Number(e.target.value || 35))))}
							sx={{
								width: 140,
								'& .MuiOutlinedInput-root': {
									borderRadius: '10px',
									bgcolor: '#f8f9fb',
								},
							}}
							inputProps={{ min: 1, max: 200 }}
						/>
						<Button
							variant="outlined"
							startIcon={<Refresh />}
							onClick={loadStandardsCatalog}
							disabled={standardsLoading || standardsActionLoading}
							sx={{
								borderRadius: '10px',
								textTransform: 'none',
								fontWeight: 500,
								fontSize: '0.8rem',
								borderColor: '#e5e7eb',
								color: '#6b7280',
								'&:hover': { borderColor: '#e5e7eb', bgcolor: '#f8f9fb' },
							}}
						>
							Refresh List
						</Button>
						<Button
							startIcon={<Refresh />}
							onClick={handleFetchStandardsCatalog}
							disabled={standardsActionLoading}
							sx={{
								borderRadius: '10px',
								textTransform: 'none',
								fontWeight: 500,
								fontSize: '0.8rem',
								bgcolor: '#111827',
								color: '#fff',
								'&:hover': { bgcolor: '#1f2937' },
							}}
						>
							Update Catalog (OJEU)
						</Button>
						<Button
							onClick={handleBulkExtractClauses}
							disabled={standardsActionLoading || standards.length === 0}
							sx={{
								borderRadius: '10px',
								textTransform: 'none',
								fontWeight: 500,
								fontSize: '0.8rem',
								bgcolor: '#111827',
								color: '#fff',
								'&:hover': { bgcolor: '#1f2937' },
							}}
						>
							Extract Clauses (All)
						</Button>
					</Box>
				</Box>

				{standardsLoading && (
					<Box display="flex" justifyContent="center" my={2}>
						<CircularProgress size={24} />
					</Box>
				)}

				{!standardsLoading && standards.length === 0 && (
					<Alert
						severity="info"
						sx={{ borderRadius: '10px' }}
					>
						No standards found in the catalog yet.
					</Alert>
				)}

				{standards.length > 0 && (
					<TableContainer
						component={Paper}
						sx={{
							borderRadius: '12px',
							border: '1px solid #eceef1',
							boxShadow: 'none',
							overflow: 'hidden',
						}}
					>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell
										sx={{
											bgcolor: '#f8f9fb',
											color: '#6b7280',
											fontWeight: 600,
											fontSize: '0.72rem',
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											borderBottom: '1px solid #f3f4f6',
										}}
									>
										Standard
									</TableCell>
									<TableCell
										sx={{
											bgcolor: '#f8f9fb',
											color: '#6b7280',
											fontWeight: 600,
											fontSize: '0.72rem',
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											borderBottom: '1px solid #f3f4f6',
										}}
									>
										Source
									</TableCell>
									<TableCell
										align="right"
										sx={{
											bgcolor: '#f8f9fb',
											color: '#6b7280',
											fontWeight: 600,
											fontSize: '0.72rem',
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											borderBottom: '1px solid #f3f4f6',
										}}
									>
										Clauses
									</TableCell>
									<TableCell
										sx={{
											bgcolor: '#f8f9fb',
											color: '#6b7280',
											fontWeight: 600,
											fontSize: '0.72rem',
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											borderBottom: '1px solid #f3f4f6',
										}}
									>
										Last Updated
									</TableCell>
									<TableCell
										align="right"
										sx={{
											bgcolor: '#f8f9fb',
											color: '#6b7280',
											fontWeight: 600,
											fontSize: '0.72rem',
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											borderBottom: '1px solid #f3f4f6',
										}}
									>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{standards.map((s) => (
									<TableRow
										key={s.code}
										hover
										sx={{
											'&:hover': { bgcolor: '#f8f9fb' },
											'& .MuiTableCell-root': {
												fontSize: '0.82rem',
												color: '#374151',
												borderBottom: '1px solid #f3f4f6',
											},
										}}
									>
										<TableCell>
											<Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
												{s.code}
											</Typography>
											{s.title && (
												<Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', display: 'block', mt: 0.25 }}>
													{s.title}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Chip
												size="small"
												label={s.source || "unknown"}
												sx={{
													height: 22,
													fontSize: '0.7rem',
													borderRadius: '6px',
													bgcolor: s.source === "manual_upload" ? '#fef9c3' : '#f3f4f6',
													color: '#374151',
												}}
											/>
										</TableCell>
										<TableCell align="right">
											<Chip
												size="small"
												label={`${s.clause_count ?? 0}`}
												sx={{
													height: 22,
													fontSize: '0.7rem',
													borderRadius: '6px',
													bgcolor: (s.clause_count ?? 0) > 0 ? '#dcfce7' : '#fef9c3',
													color: '#111827',
												}}
											/>
										</TableCell>
										<TableCell>
											<Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>
												{s.last_fetched_at ? format(new Date(s.last_fetched_at), "yyyy-MM-dd HH:mm") : "—"}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Button
												size="small"
												variant="outlined"
												onClick={() => handleExtractClauses(s.code)}
												disabled={standardsActionLoading}
												sx={{
													borderRadius: '10px',
													textTransform: 'none',
													fontWeight: 500,
													fontSize: '0.8rem',
													borderColor: '#e5e7eb',
													color: '#6b7280',
													'&:hover': { borderColor: '#e5e7eb', bgcolor: '#f8f9fb' },
												}}
											>
												Extract Clauses
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>
		);
	};

	const renderChatAutomation = () => {
		return (
			<Box>
				<Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 1 }}>
					Chat → Workflow Automation
				</Typography>
				<Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', mb: 2 }}>
					Control when a workflow is automatically created for device/compliance-type questions. Truncation and “…” are UI-only; stored names remain full.
				</Typography>

				<Card
					sx={{
						bgcolor: '#fff',
						border: '1px solid #eceef1',
						borderRadius: '12px',
						boxShadow: 'none',
					}}
				>
					<CardContent>
						<FormControlLabel
							control={
								<Switch
									checked={autoCreateWorkflowEnabled}
									onChange={(e) => {
										const next = e.target.checked;
										setAutoCreateWorkflowEnabled(next);
										localStorage.setItem("gamora_workflow_auto_create_enabled", next ? "true" : "false");
										setSuccess(next ? "Auto-create workflows enabled." : "Auto-create workflows disabled.");
									}}
								/>
							}
							label="Auto-create workflow when question is clearly a device/compliance project"
							sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.82rem', color: '#374151' } }}
						/>

						<Box sx={{ mt: 2, maxWidth: 520 }}>
							<Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', mb: 0.5 }}>
								Auto-create threshold (higher = fewer auto-created workflows)
							</Typography>
							<Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', display: "block", mb: 1 }}>
								Current: {autoCreateWorkflowThreshold}. Recommended default: 5.
							</Typography>
							<Slider
								value={autoCreateWorkflowThreshold}
								min={3}
								max={7}
								step={1}
								marks
								valueLabelDisplay="auto"
								disabled={!autoCreateWorkflowEnabled}
								onChange={(_, value) => {
									const v = Array.isArray(value) ? value[0] : value;
									setAutoCreateWorkflowThreshold(v);
									localStorage.setItem("gamora_workflow_auto_create_threshold", String(v));
								}}
								sx={{
									color: '#111827',
									'& .MuiSlider-track': { bgcolor: '#111827' },
									'& .MuiSlider-thumb': { bgcolor: '#111827' },
								}}
							/>
						</Box>
					</CardContent>
				</Card>
			</Box>
		);
	};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography
            component="h1"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 650,
              color: '#111827',
              letterSpacing: '-0.02em',
            }}
          >
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', mt: 0.25 }}>
            Manage compliance workflows, forms, and standards
          </Typography>
        </Box>
        <Button
          startIcon={<Add />}
          onClick={() => setCreateWorkflowOpen(true)}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            bgcolor: '#111827',
            color: '#fff',
            '&:hover': { bgcolor: '#1f2937' },
          }}
        >
          New Workflow
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #eceef1', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTabs-indicator': { bgcolor: '#111827' },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.82rem',
              color: '#6b7280',
            },
            '& .Mui-selected': { color: '#111827' },
          }}
        >
          <Tab label="Workflows" />
          <Tab label="Forms" />
          <Tab label="Service Providers" />
          <Tab label="Standards Catalog" />
          <Tab label="Chat Automation" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 2 }}>
              Active Workflows
            </Typography>
            {loading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && Array.isArray(workflows) && workflows.length === 0 && (
              <Box
                sx={{
                  border: '1px solid #eceef1',
                  borderRadius: '12px',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  No workflows found. Create a new workflow to get started.
                </Typography>
              </Box>
            )}
            {!loading && Array.isArray(workflows) && workflows.map(renderWorkflowCard)}
          </Box>

          <Box sx={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
            {selectedWorkflow && renderWorkflowSteps()}
          </Box>
        </Box>
      )}

      {activeTab === 1 && renderForms()}
      {activeTab === 2 && renderProviders()}
      {activeTab === 3 && renderStandardsCatalog()}
      {activeTab === 4 && renderChatAutomation()}

      {/* Create Workflow Dialog */}
      {renderCreateWorkflowDialog()}

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: '10px' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ borderRadius: '10px' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ComplianceDashboard;
